// src/Pages/Split/SplitPayer.jsx — v8.1
// ─────────────────────────────────────────────────────────────────────────
// Blitz Split — the payer side of the card:
//   BlitzPayCompanyField    server-side search of BlitzPay companies
//   ContactPickerDialog     POPUP: who at that company gets the invoice
//   BilledToStrip           pinned identity strip on the split card
//
// v8.1: the contact picker is a Dialog, not an inline list. Picking a
// company immediately pops the dialog — one decision, full focus, done.
// The dialog is fullScreen on mobile, matching the platform's dialog
// patterns. The invoice is addressed to the chosen person's email, which
// is exactly what authorizes them in unified_invoice_action.
// ─────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Typography, TextField, Paper, Avatar, IconButton, Autocomplete,
  InputAdornment, CircularProgress, Chip, MenuItem, Select, FormControl,
  Alert, Stack, Tooltip, Dialog, DialogContent, useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Search as SearchIcon, CheckCircle as CheckCircleIcon,
  Business as BusinessIcon, Person as PersonIcon, Edit as EditIcon,
  MailOutline as ApIcon, Close as CloseIcon,
} from "@mui/icons-material";

import client from "../../../API";
import {
  SPLIT_YELLOW, SPLIT_YELLOW_DARK, SPLIT_YELLOW_DEEP, SPLIT_INK,
  SPLIT_TINT, SPLIT_BORDER, TERMS, fadeIn, fadeUp,
} from "./SplitTokens";

// ═══════════════════════════════════════════════════════════════════════════
// BlitzPayCompanyField — server-side search via /bbc/companies, with a
// graceful fallback to the old client-side filter while the endpoint ships.
// ═══════════════════════════════════════════════════════════════════════════
export function BlitzPayCompanyField({ value, onChange }) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCompanies = useCallback(async (query) => {
    if (!query || query.length < 2) { setOptions([]); return; }
    setLoading(true);
    try {
      const res = await client.bbc.searchCompanies(query, 20);
      setOptions(res?.companies || []);
    } catch {
      try {
        const result = await client.companies.list({ limit: 500, offset: 0 });
        const arr = Array.isArray(result) ? result : result?.companies || [];
        setOptions(
          arr.filter(
            (c) =>
              c.blitzpay_account === true &&
              c.name?.toLowerCase().includes(query.toLowerCase()),
          ).slice(0, 20),
        );
      } catch {
        setOptions([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchCompanies(inputValue), 300);
    return () => clearTimeout(t);
  }, [inputValue, fetchCompanies]);

  return (
    <Autocomplete
      value={value}
      onChange={(_, v) => onChange(v)}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      options={options}
      getOptionLabel={(o) => o?.name || ""}
      isOptionEqualToValue={(o, v) => o?.id === v?.id}
      loading={loading}
      filterOptions={(x) => x}
      noOptionsText={
        inputValue.length < 2
          ? "Type at least 2 characters…"
          : "No BlitzPay companies match — Split requires a BlitzPay-enabled payer"
      }
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id} sx={{ py: "10px !important" }}>
          <Box sx={{ position: "relative", flexShrink: 0, mr: 1.5 }}>
            <Box sx={{ position: "absolute", inset: -2.5, borderRadius: "50%",
              background: `linear-gradient(135deg, ${SPLIT_YELLOW}, ${alpha(SPLIT_YELLOW, 0.4)})` }} />
            <Avatar src={option.phphref || ""} alt={option.name}
              sx={{ width: 38, height: 38, bgcolor: SPLIT_TINT, color: SPLIT_YELLOW_DEEP,
                fontSize: 15, fontWeight: 800, position: "relative", border: "2.5px solid white" }}>
              {option.name?.[0]?.toUpperCase()}
            </Avatar>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>{option.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {option.account_status || "Active"} · ⚡ BlitzPay
            </Typography>
          </Box>
          <Chip label="Bill" size="small"
            sx={{ fontWeight: 700, fontSize: "0.7rem", height: 24, borderRadius: 1.5,
              bgcolor: SPLIT_YELLOW, color: SPLIT_INK }} />
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Company to bill"
          placeholder="Search BlitzPay companies…"
          required
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon sx={{ color: loading ? SPLIT_YELLOW_DARK : "text.secondary", mr: 1 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: { borderRadius: 2.5, bgcolor: "background.paper" },
          }}
        />
      )}
      sx={{ width: "100%" }}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ContactPickerDialog — "Who at {company} gets this invoice?"
//
// Pops automatically when a company is chosen. Picking a person calls
// onPick(contact) — the parent stores it and closes. The AP inbox is an
// explicit dashed fallback at the bottom, never the default.
//
// onPick(contact): { kind: 'user'|'ap', id?, name, email, phphref?,
//                    is_supervisor? }
// ═══════════════════════════════════════════════════════════════════════════
export function ContactPickerDialog({ open, company, value, onPick, onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [contacts, setContacts] = useState([]);
  const [apContact, setApContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!open || !company?.id) return;
    let alive = true;
    setLoading(true);
    setError(null);
    setFilter("");
    client.bbc.companyContacts(company.id)
      .then((res) => {
        if (!alive) return;
        setContacts(res?.contacts || []);
        setApContact(res?.ap_contact || null);
      })
      .catch(() => alive && setError("Couldn't load people at this company."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, company?.id]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) =>
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q));
  }, [contacts, filter]);

  const pickUser = (c) => onPick({
    kind: "user",
    id: c.id,
    name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
    email: c.email,
    phphref: c.phphref,
    is_supervisor: c.is_supervisor,
  });

  const pickAp = () => apContact && onPick({
    kind: "ap", id: null, name: apContact.name, email: apContact.email,
  });

  if (!company) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 4,
          overflow: "hidden",
          bgcolor: "background.paper",
        },
      }}
    >
      {/* ── Yellow header — the question IS the header ─────────────────── */}
      <Box sx={{
        p: { xs: 2.5, sm: 3 },
        background: `linear-gradient(135deg, ${SPLIT_YELLOW_DEEP} 0%, ${SPLIT_YELLOW} 70%, #FFD45E 100%)`,
        color: SPLIT_INK, position: "relative",
      }}>
        <IconButton onClick={onClose} size="small" aria-label="Close" sx={{
          position: "absolute", top: 12, right: 12, color: SPLIT_INK,
          bgcolor: alpha(SPLIT_INK, 0.08), "&:hover": { bgcolor: alpha(SPLIT_INK, 0.16) },
        }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: 5 }}>
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            <Box sx={{ position: "absolute", inset: -3, borderRadius: "50%",
              background: alpha(SPLIT_INK, 0.18) }} />
            <Avatar src={company.phphref || ""} sx={{
              width: 48, height: 48, bgcolor: "background.paper",
              color: SPLIT_YELLOW_DEEP, fontWeight: 900, fontSize: "1.1rem",
              position: "relative", border: "3px solid", borderColor: alpha("#fff", 0.9),
            }}>
              {company.name?.[0]?.toUpperCase()}
            </Avatar>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 800, opacity: 0.75,
              textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "0.6rem",
              display: "block" }}>
              {company.name} · ⚡ BlitzPay
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: "-0.02em",
              lineHeight: 1.2 }}>
              Who gets this invoice?
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.8 }}>
              They'll receive it and approve it in-app.
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: "background.paper" }}>
        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center",
            gap: 1.25, py: 6 }}>
            <CircularProgress size={20} sx={{ color: SPLIT_YELLOW_DARK }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Loading people at {company.name}…
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

        {!loading && !error && (
          <>
            {contacts.length > 6 && (
              <TextField fullWidth size="small" autoFocus value={filter}
                placeholder={`Search ${contacts.length} people…`}
                onChange={(e) => setFilter(e.target.value)}
                sx={{ mb: 1.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 17, color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2.5 },
                }} />
            )}

            <Stack spacing={0.75} sx={{ maxHeight: fullScreen ? "none" : 380, overflowY: "auto", pr: 0.5 }}>
              {filtered.map((c, i) => {
                const selected = value?.kind === "user" && value?.id === c.id;
                return (
                  <Paper key={c.id} variant="outlined" onClick={() => pickUser(c)} sx={{
                    p: 1.5, borderRadius: 2.5, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 1.5,
                    borderColor: selected ? SPLIT_YELLOW_DARK : "grey.200",
                    borderWidth: selected ? 2 : 1,
                    bgcolor: selected ? SPLIT_TINT : "background.paper",
                    transition: "all 0.15s ease",
                    animation: `${fadeUp} 0.2s ease both`,
                    animationDelay: `${Math.min(i * 0.03, 0.3)}s`,
                    "&:hover": { borderColor: SPLIT_YELLOW_DARK,
                      bgcolor: alpha(SPLIT_YELLOW, 0.04), transform: "translateX(3px)" },
                  }}>
                    <Box sx={{ position: "relative", flexShrink: 0 }}>
                      <Box sx={{ position: "absolute", inset: -2.5, borderRadius: "50%",
                        background: selected
                          ? `linear-gradient(135deg, ${SPLIT_YELLOW}, ${SPLIT_YELLOW_DARK})`
                          : `linear-gradient(135deg, ${alpha(SPLIT_YELLOW, 0.6)}, ${alpha(SPLIT_YELLOW, 0.2)})` }} />
                      <Avatar src={c.phphref || undefined} sx={{
                        width: 40, height: 40, fontSize: "0.95rem", fontWeight: 800,
                        bgcolor: selected ? SPLIT_YELLOW : "grey.100",
                        color: selected ? SPLIT_INK : "text.secondary",
                        position: "relative", border: "2.5px solid white",
                      }}>
                        {(c.first_name?.[0] || <PersonIcon sx={{ fontSize: 18 }} />)}
                      </Avatar>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.9rem" }} noWrap>
                          {c.first_name} {c.last_name}
                        </Typography>
                        {c.is_supervisor && (
                          <Chip label="Admin" size="small" sx={{ height: 16, fontSize: "0.56rem",
                            fontWeight: 800, bgcolor: alpha(SPLIT_INK, 0.08),
                            "& .MuiChip-label": { px: 0.6 } }} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary"
                        sx={{ fontSize: "0.7rem" }} noWrap>
                        {c.email}
                      </Typography>
                    </Box>
                    {selected
                      ? <CheckCircleIcon sx={{ fontSize: 22, color: SPLIT_YELLOW_DARK }} />
                      : <Chip label="Bill" size="small" sx={{ height: 26, fontSize: "0.7rem",
                          fontWeight: 700, borderRadius: 1.5, bgcolor: SPLIT_YELLOW, color: SPLIT_INK }} />}
                  </Paper>
                );
              })}

              {filtered.length === 0 && contacts.length > 0 && (
                <Typography variant="caption" color="text.secondary"
                  sx={{ py: 2, textAlign: "center" }}>
                  No one matches "{filter}".
                </Typography>
              )}

              {contacts.length === 0 && (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="caption">
                    No platform users found at {company.name}.
                    {apContact
                      ? " You can bill their AP inbox below."
                      : " Ask your account manager to add a contact."}
                  </Typography>
                </Alert>
              )}
            </Stack>

            {/* AP inbox — explicit fallback, visually distinct, never default */}
            {apContact && (
              <Paper variant="outlined" onClick={pickAp} sx={{
                mt: 1.5, p: 1.5, borderRadius: 2.5, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 1.5, borderStyle: "dashed",
                borderColor: value?.kind === "ap" ? SPLIT_YELLOW_DARK : "grey.300",
                bgcolor: value?.kind === "ap" ? SPLIT_TINT : "grey.50",
                "&:hover": { borderColor: SPLIT_YELLOW_DARK },
              }}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: "grey.200", color: "text.secondary" }}>
                  <ApIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.88rem" }} noWrap>
                    {apContact.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ fontSize: "0.7rem" }} noWrap>
                    {apContact.email} · email approval, not in-app
                  </Typography>
                </Box>
                {value?.kind === "ap" && (
                  <CheckCircleIcon sx={{ fontSize: 22, color: SPLIT_YELLOW_DARK }} />
                )}
              </Paper>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// BilledToStrip — pinned identity strip. Person-first. Tapping the body
// reopens the contact dialog; the pencil reopens company search.
// ═══════════════════════════════════════════════════════════════════════════
export function BilledToStrip({ payer, contact, paymentTerm, onTerm, onEditCompany, onEditContact }) {
  if (!payer) {
    return (
      <Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "center", gap: 1.25,
        bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "grey.100" }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: "grey.200", color: "text.disabled" }}>
          <BusinessIcon sx={{ fontSize: 17 }} />
        </Avatar>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          No payer yet — pick the company to bill above.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      px: 2.5, py: 1.5, display: "flex", alignItems: "center", gap: 1.5,
      bgcolor: SPLIT_TINT, borderBottom: "1px solid", borderColor: SPLIT_BORDER,
      animation: `${fadeIn} 0.2s ease`,
    }}>
      <Box
        onClick={onEditContact}
        sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0,
          cursor: "pointer", borderRadius: 2, mx: -1, px: 1, py: 0.25,
          "&:hover": { bgcolor: alpha(SPLIT_YELLOW, 0.06) } }}
      >
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Box sx={{ position: "absolute", inset: -2.5, borderRadius: "50%",
            background: `linear-gradient(135deg, ${SPLIT_YELLOW}, ${alpha(SPLIT_YELLOW, 0.4)})` }} />
          <Avatar src={(contact?.phphref || payer.phphref) || ""}
            sx={{ width: 34, height: 34, bgcolor: "background.paper", color: SPLIT_YELLOW_DEEP,
              fontWeight: 800, fontSize: "0.85rem", position: "relative",
              border: "2.5px solid white" }}>
            {(contact?.name || payer.name)?.[0]?.toUpperCase()}
          </Avatar>
          {contact && (
            <CheckCircleIcon sx={{ position: "absolute", bottom: -2, right: -2, fontSize: 13,
              color: SPLIT_YELLOW_DARK, bgcolor: "white", borderRadius: "50%", zIndex: 2 }} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled",
            textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.58rem",
            display: "block" }}>
            Billed to
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>
            {contact ? contact.name : "Pick a person"}
            <Typography component="span" variant="caption"
              color={contact ? "text.secondary" : SPLIT_YELLOW_DEEP}
              sx={{ ml: 0.75, fontWeight: contact ? 500 : 700 }}>
              {contact
                ? `${payer.name} · ${contact.kind === "ap" ? "AP inbox" : "⚡ approves in-app"}`
                : `at ${payer.name} — tap to choose`}
            </Typography>
          </Typography>
        </Box>
      </Box>
      <FormControl size="small">
        <Select value={paymentTerm} onChange={(e) => onTerm(e.target.value)}
          sx={{ borderRadius: 2, fontWeight: 700, fontSize: "0.78rem", height: 32,
            bgcolor: "background.paper" }}>
          {TERMS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </Select>
      </FormControl>
      <Tooltip title="Change company">
        <IconButton size="small" onClick={onEditCompany} sx={{ color: "text.secondary" }}>
          <EditIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}